import type { RootState } from 'store';
import { store } from '../store';
import { validateZipCode } from './FormUtils';
import { capitalizeWords } from './CommonUtils.misc';
import { getMonthAndDate } from './CommonUtils';
import { getDecoratingTypeList, getPrintTypeList } from './PrintTypeDecorUtils';
import { getDesignMinQuantity } from './DesignUtils.misc';
import {
  formatDeliveryDateRange,
  formatSingleDate,
  getCustomDeliveryDateRange,
  getEstDeliveyTimeEST,
  adjustWeekendToNextBusinessDay,
} from './ShippingUtils';
import { isDesignRejected } from './LicensingUtils';
import type {
  IRegularOrderForm,
  IReviewOrderItem,
  ICustomItem,
  IQuantitySizeItem,
  IRegularOrderPayload,
  SuggestedFormAddress,
} from '@/common/interfaces/order.interface';
import type { ISuggestedAddress } from '@/common/interfaces/error.interface';
import {
  CART_SUMMARY_PAY_LATER_DESC_TEXT,
  GROUP_ORDER_HEADER_SECONDARY_TEXT,
  REG_ORDER_STEPS_WITH_CUSTOMIZATION_COUNT,
  REG_ORDER_STEP_COUNT,
  SHIPPING_FIELD_ERROR_MSGS,
} from '@/constants/Order';
import type {
  ICartItemDetail,
  ICartResponse,
  ISkuShippingTimeline,
  ITimelineElement,
} from '@/common/interfaces/cart.interface';
import type { IDesignLicenses } from '@/common/interfaces/design.interface';
import type { IDecoratingItem, IPrintTypeItem } from '@/common/interfaces/print-decor.interface';
import {
  MADE_TO_ORDER_SHIPPING,
  FLASH_SHIPPING,
  EXPEDITED_SHIPPING,
  STANDARD_SHIPPING,
  AUTO_GUARANTEE_SHIP_TYPES,
  ECONOMY_SHIPPING_KEY,
} from '@/constants/ShipTypes';
import type { SHIPPING_DAYS } from '@/constants/ShipTypes';

/** Helper function to get guarantee value based on input */
const getGuaranteeValue = (guarantee: string | undefined) => {
  if (guarantee === 'FREE') return 0;
  if (!guarantee || guarantee === '0') return undefined;
  return Number(guarantee);
};

/**
 * Method to prepare data for regular order steps
 * @param cartItems - Array of cart items
 */
export const prepareRegOrderStepsData = (cartItems: ICartResponse, userOrg?: string) => {
  if (!cartItems || cartItems?.length === 0) {
    return { reviewItems: [], quantitySizeItems: [], sizes: {}, customizedDesigns: [] };
  }

  const sizeValue: Record<number, Record<string, string>> = {};
  const reviewItems: IReviewOrderItem[] = [];
  const quantitySizeItems: IQuantitySizeItem[] = [];
  const customizedDesigns: ICustomItem[] = [];

  cartItems.forEach((cartItem) => {
    const { designId } = cartItem;
    const sizeObject: Record<string, string> = {};
    const codeObject: Record<string, number> = {};
    const designRejectionInfo = isDesignRejected(
      cartItem.fromDesignTool,
      cartItem.designLicensing,
      cartItem.designPricing,
      userOrg
    );

    cartItem.apparelSizes.split(';').forEach((entry) => {
      const [code, size] = entry.split(':');

      if (!size) return;

      sizeObject[size] = '';
      codeObject[size] = parseInt(code, 10);
    });

    sizeValue[designId] = sizeObject;

    reviewItems.push({
      id: designId,
      name: cartItem.proofTitle,
      styleCode: cartItem.apparelStyleCode,
      activeColor: {
        color: cartItem.apparelColor,
        hex: cartItem.apparelHex,
      },
      price: cartItem.price,
      productTitle: cartItem.apparelName || cartItem.apparelBrand,
      brand: cartItem.apparelBrand,
      designFrontImage: cartItem.frontImage,
      designBackImage: cartItem.backImage,
      designImgPrimary: cartItem.designImgPrimary,
      designImgSecondary: cartItem.designImgSecondary,
      printTypes: getPrintTypeList(
        cartItem.printTypeInfo ? (cartItem.printTypeInfo as IPrintTypeItem[]) : cartItem.printTypes
      ),
      decorMethods: getDecoratingTypeList(
        cartItem.decorMethodInfo?.length
          ? (cartItem.decorMethodInfo as IDecoratingItem[])
          : cartItem.decoratingMethod
      ),
      item: cartItem,
      designRejectionInfo,
      priceList: cartItem.priceList || [],
    });

    let minQuant = getDesignMinQuantity(
      cartItem.apparelColor.toLowerCase().includes('mto'),
      cartItem.isFlashEligible
    );

    if (cartItem.minQuant) minQuant = cartItem.minQuant;

    quantitySizeItems.push({
      id: designId,
      sizes: sizeObject,
      codes: codeObject,
      price: cartItem.price,
      name: cartItem.proofTitle,
      productTitle: cartItem.apparelName || cartItem.apparelBrand,
      brand: cartItem.apparelBrand,
      designFrontImage: cartItem.frontImage,
      minQuantity: minQuant,
      item: cartItem,
      designRejectionInfo,
      priceList: cartItem.priceList || [],
    });

    if (cartItem.hasCustomNames || cartItem.hasCustomNumbers) {
      customizedDesigns.push({
        id: designId,
        hasCustomNames: cartItem.hasCustomNames,
        hasCustomNumbers: cartItem.hasCustomNumbers,
        imgUrl: cartItem.primaryImage,
        name: cartItem.proofTitle,
        productTitle: cartItem.apparelName || cartItem.apparelBrand,
        brand: cartItem.apparelBrand,
        designRejectionInfo,
      });
    }
  });

  return { reviewItems, quantitySizeItems, sizes: sizeValue, customizedDesigns };
};

/** Function to get Title based on custom names & numbers of items
 * @param items - Array of items
 * @param isMobile - Boolean to check if device is mobile
 */
export const getCustomStepTitle = (items: ICustomItem[], isMobile: boolean) => {
  const hasCustomNames = items.some((item) => item.hasCustomNames);
  const hasCustomNumbers = items.some((item) => item.hasCustomNumbers);

  if (isMobile) {
    return 'Upload CSV';
  }

  if (hasCustomNames && hasCustomNumbers) {
    return 'Upload CSV for Personalized Names & Numbers';
  }
  if (hasCustomNames && !hasCustomNumbers) {
    return 'Upload CSV for Personalized Names';
  }
  if (!hasCustomNames && hasCustomNumbers) {
    return 'Upload CSV for Personalized Numbers';
  }

  return '';
};

/** Function to get SubTitle based on custom names & numbers of items
 * @param hasCustomNames - Boolean to check if item has custom names
 * @param hasCustomNumbers - Boolean to check if item has custom numbers
 * @param isMobile - Boolean to check if device is mobile
 */
export const getCustomStepSubTitleForItem = (
  hasCustomNames: boolean,
  hasCustomNumbers: boolean,
  isMobile: boolean
) => {
  let message = '';

  if (hasCustomNames || hasCustomNumbers) {
    let itemText = '';

    if (hasCustomNames && hasCustomNumbers) {
      itemText = 'names & numbers';
    } else if (hasCustomNames) {
      itemText = 'names';
    } else {
      itemText = 'numbers';
    }

    message = `We need a list of the ${itemText} you want us to print. Download our template, add the ${itemText} to it, and then upload it below.`;
  }

  /* if mobile then shorten text */
  if (isMobile && message.includes('.')) {
    message = message.split('.').slice(1).join('.').trim();
  }

  return message;
};

/**
 * Function to download sample CSV for participant upload
 * @param designId - designId to be used in the file name
 * @param userId - userId to be used in the file name
 */
export const downloadSampleParticipantUploadCSV = (designId: number, userId: number) => {
  const columns = ['Name', 'Email', 'Size', 'Quantity', 'Custom Name', 'Custom Number'];

  let csvString = columns.join(',');
  csvString += '\n';

  const sampleData = ['Sample Name', 'sample@sample.com', 'M', '1', 'Sample', '1'];

  csvString += sampleData.join(',');

  const hiddenElement = document.createElement('a');
  hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csvString)}`;
  hiddenElement.target = '';

  hiddenElement.download = `Participants_${designId}_${userId}.csv`;
  hiddenElement.click();
};

/** Function to validate shipping details
 * @param value - Value to be validated
 */
export const validateShippingZipCode = (value: string) => {
  const isZipValid = validateZipCode(value);

  if (!isZipValid) {
    return SHIPPING_FIELD_ERROR_MSGS.ZIP_INVALID;
  }

  return true;
};

/**
 * Method to validate CSV file
 * @param fileName - Name of the file
 * @returns
 */
export const validateCsvFile = (fileName: string) => {
  const allowedExtensions = /(\.csv)$/i;

  if (!allowedExtensions.exec(fileName)) {
    return false;
  }

  return true;
};

/**
 * Method to find valid step based on conditions array
 * @param totalSteps - Number of Steps
 * @param conditionArray - Condition array to check
 */
export const findValidStepBasedOnCondition = (totalSteps: number, conditionArray: boolean[]) => {
  let step = totalSteps;

  while (step >= 0) {
    const conditionSlice = conditionArray.slice(0, step + 1);

    if (conditionSlice.length === 1) {
      return conditionSlice[0] ? step + 1 : step;
    }

    if (conditionSlice.every((condition) => condition)) {
      return step + 1;
    }

    step -= 1;
  }

  return 0;
};

/** Get estimated ship date */
export const getDueDateFromCurrentDate = (daysToAdd: number) => {
  const currentDate = new Date();
  const dueDate = new Date(currentDate.setDate(currentDate.getDate() + daysToAdd));
  const month = dueDate.toLocaleString('default', { month: 'long' });

  return `${month} ${dueDate.getDate()}, ${dueDate.getFullYear()}`;
};

/**
 * Method to transform to sizes format to send
 * Format is ; separated values with each size as "code:size:quantity"
 * @param objectCode - Size code object with value as code
 * @param size - Size object with value as quantity
 */
export const getItemSizePayload = (
  objectCode: Record<string, number>,
  size: Record<string, string | number>
) => {
  const resultStrings = Object.entries(objectCode)
    .filter(([key]) => size[key] && size[key] !== '')
    .map(([key, value]) => `${value}:${key}:${size[key]}`);

  const result = resultStrings.join(';');

  return result;
};

const shipTypeDeliveryDateKeyMap: Record<string, 'Standard' | 'Flash' | 'MTO' | 'Expedited'> = {
  mto: MADE_TO_ORDER_SHIPPING,
  flash: FLASH_SHIPPING,
  expedited: EXPEDITED_SHIPPING,
  standard: STANDARD_SHIPPING,
};

/**
 * Method to get regular order payload
 * @param details - Form Details
 * @param itemInfo - Item Info Array
 */
export const getRegularOrderPayload = (
  details: IRegularOrderForm,
  itemInfo: IQuantitySizeItem[],
  simulateError: boolean,
  showCollegiateInfoForm: boolean,
  isPayLaterSelected: boolean,
  regOrderNotes?: { [key: string]: string },
  estimatedDeliveryDate?: string,
  fromDeliveryDate?: string,
  toDeliveryDate?: string
) => {
  const cardNumber = details.creditCard.replace(/\s/g, '');
  const expMonthYear = details.expiryDate.split('/');
  const expirationDate = `${String(expMonthYear[1])},${expMonthYear[0]}`;

  const dueDate =
    estimatedDeliveryDate ??
    getEstDeliveyTimeEST(shipTypeDeliveryDateKeyMap[details?.shipTypeKey ?? 'Standard'], true);

  const orderPayload: IRegularOrderPayload = {
    name: details.orderName,
    dueDate,
    shippingName: details.shippingName,
    street1: details.street1,
    street2: details.street2 || null,
    city: details.city,
    isFlashOrder: details?.shipTypeKey === 'flash',
    isExpeditedOrder: details?.shipTypeKey === 'expedited',
    isMadeToOrder: details?.shipTypeKey === 'mto',
    isEconomyOrder: details?.shipTypeKey === ECONOMY_SHIPPING_KEY,
    state: (details.state as { value: string }).value,
    zipCode: details.shippingZipCode,
    groupOrderDate: null,
    paymentExpected: details.amount,
    salesTax: details.salesTax,
    shipping: details.shipping,
    cardNumber,
    cardName: details.name,
    expirationDate,
    cardCode: details.cvv,
    billingZipCode: details.zipCode,
    items: [] as Record<string, number | string>[],
    simulateError,
    makePayment: !isPayLaterSelected,
    ccEmail: isPayLaterSelected ? details.ccEmail : '',
    nonStdPlacementConfirm: details.nonStdPlacementConfirm,
    guarantee: getGuaranteeValue(details.guarantee),
    fromDeliveryDate: fromDeliveryDate ?? '',
    toDeliveryDate: toDeliveryDate ?? '',
  };

  orderPayload.items = itemInfo.map((item) => {
    let discount: number | undefined;
    let discountInPercentage: number | undefined;
    let originalPrice: number | undefined;

    if (details.sizes[item.id].strikedPricePerUnit) {
      discount = Number(
        (
          Number(details.sizes[item.id].strikedPricePerUnit) -
          Number(details.sizes[item.id].pricePerUnit)
        ).toFixed(2)
      );
      originalPrice = Number(details.sizes[item.id].strikedPricePerUnit);
      discountInPercentage = item.item?.designPricing.discount;
    } else {
      originalPrice = Number(details.sizes[item.id].pricePerUnit);
    }

    return {
      designId: item.id,
      price: details.sizes[item.id].pricePerUnit,
      sizes: getItemSizePayload(item.codes, details.sizes[item.id]),
      numUnits: details.sizes[item.id].total,
      customFile: details.customItem[item.id] ? details.customItem[item.id].s3Path : '',
      ...(originalPrice && { originalPrice }),
      ...(discount && { discount }),
      ...(discountInPercentage && { discountInPercentage }),
      ...(regOrderNotes &&
        item.item?.id &&
        regOrderNotes[item.item?.id] && { notes: regOrderNotes[item.item?.id] }),
    };
  });

  if (showCollegiateInfoForm) {
    orderPayload.advisorName = details.advisorName ?? '';
    orderPayload.advisorPhone = details.advisorPhone ?? '';
    orderPayload.quantity = Number(details.quantity) ?? 0;
    orderPayload.payer =
      typeof details.whoPaying === 'string' ? details.whoPaying : details.whoPaying?.value ?? '';
    orderPayload.purpose =
      typeof details.generalPurpose === 'string'
        ? details.generalPurpose
        : details.generalPurpose?.value ?? '';
    orderPayload.schoolId = details.schoolId;
    orderPayload.schoolName = details.schoolInfo;
    orderPayload.organizationName = details.orgInfo;
    orderPayload.collegiateClientName = details.fullName;
    orderPayload.collegiateClientPhone = details.phone;
    orderPayload.collegiateClientEmail = details.email;
  }

  return orderPayload;
};

/**
 * Function that tells if the order is recently placed
 * @param orderDate - date at which order has been placed
 */
export const isRecentlyPlacedOrder = (orderDate: string): boolean => {
  if (!orderDate) {
    return false;
  }

  const happenedAt = new Date(orderDate);
  const now = new Date();

  const timeDifference = now.getTime() - happenedAt.getTime();

  return timeDifference < 60000;
};

/**
 * Function to return if user can place an order
 * @param details - Form Values
 * @param step - Step number
 * @param hasCustomSTep - Check if we have custom step
 * @param showCollegiateInfoForm - Boolean to check if collegiate info form is shown.
 */
export const canPlaceRegularOrder = (
  details: IRegularOrderForm,
  step: number,
  hasCustomStep: boolean,
  showCollegiateInfoForm: boolean
) => {
  const { sizes } = details;

  if (!details.amount) return false;

  const invalidSizes = sizes
    ? Object.entries(sizes).filter(([, sizeDetails]) => sizeDetails.total === 0)
    : [];

  if (invalidSizes.length > 0) {
    return false;
  }

  const isPriceZeroItem = sizes
    ? Object.entries(sizes).filter(([, sizeDetails]) => !sizeDetails.pricePerUnit)
    : [];

  if (isPriceZeroItem.length > 0) {
    return false;
  }

  let validateStep;

  if ((hasCustomStep && !showCollegiateInfoForm) || (showCollegiateInfoForm && !hasCustomStep)) {
    validateStep = step === REG_ORDER_STEPS_WITH_CUSTOMIZATION_COUNT;
  } else if (hasCustomStep && showCollegiateInfoForm) {
    validateStep = step === REG_ORDER_STEPS_WITH_CUSTOMIZATION_COUNT + 1;
  } else {
    validateStep = step === REG_ORDER_STEP_COUNT;
  }

  return validateStep;
};

/**
 * To check if the cart has any item with zero price
 * @param details - Form Values
 * @returns true or false
 */
export const isCartHasZeroPriceItem = (details: IRegularOrderForm) => {
  const { sizes } = details;

  const isPriceZeroItem = sizes
    ? Object.entries(sizes).filter(([, sizeDetails]) => !sizeDetails.pricePerUnit)
    : [];

  if (isPriceZeroItem.length > 0) {
    return true;
  }

  return false;
};

/**
 *
 * @param size
 * @returns is size is valid or not
 */
export const isValidSize = (size: string) => {
  const sizes = [
    'NA',
    'OS',
    '2XS',
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    '2XL',
    '3XL',
    '4XL',
    '5XL',
    '6XL',
    '7XL',
  ];

  return sizes.find((validSize) => size.toUpperCase() === validSize);
};
/**
 * Method to form address object
 * @param address - Address object
 * @param shippingName - Shipping Name
 */
export const formAddress = (address: ISuggestedAddress, shippingName: string) => {
  const streets = address.AddressLine.toString();

  return {
    shippingName,
    street1: capitalizeWords(streets.split(',')[0]),
    street2: streets.split(',')[1] ? capitalizeWords(streets.split(',')[1]) : '',
    city: capitalizeWords(address.PoliticalDivision2),
    state: address.PoliticalDivision1,
    zipCode: address.PostcodePrimaryLow,
  };
};

/**
 * Method to get suggested address
 * @param suggestions - Suggested Address
 * @param shippingName - Shipping Name
 */
export const getSuggestedAddress = (
  suggestions: ISuggestedAddress | ISuggestedAddress[],
  shippingName: string
) => {
  const suggestedAddress: SuggestedFormAddress[] = [];

  if (Array.isArray(suggestions)) {
    suggestions.forEach((suggestion) => {
      const formedAddress = formAddress(suggestion, shippingName);

      /** Checking for duplicates, using json.stringify assuming object will not be nested */
      const exists = suggestedAddress.find(
        (itm: SuggestedFormAddress) => JSON.stringify(itm) === JSON.stringify(formedAddress)
      );

      if (!exists) {
        suggestedAddress.push(formedAddress);
      }
    });
  } else {
    const formedAddress = formAddress(suggestions, shippingName);
    suggestedAddress.push(formedAddress);
  }

  return suggestedAddress;
};

/**
 * Method to get design licensing records
 * @param designs - Arrays of cart items
 */
export const getDesignLicensingRecords = (designs: ICartResponse) => {
  if (!designs || designs.length === 0) {
    return [];
  }

  const designLicensingRecs = designs
    .filter((designRec) => designRec && designRec.designLicensing)
    .map((designRec) => designRec.designLicensing);

  return designLicensingRecs;
};

export const getDeliveryDate = (
  shippingType?: keyof typeof SHIPPING_DAYS,
  activeItemRestockDate?: string,
  showRange?: boolean,
  useCustomFormat?: boolean,
  getMaxDate?: boolean,
  timelineChangesEnabled?: boolean
) => {
  const estDateMax = getEstDeliveyTimeEST(
    shippingType ?? 'Standard',
    true,
    activeItemRestockDate,
    false
  );

  const estDateMaxString = getEstDeliveyTimeEST(
    shippingType ?? 'Standard',
    false,
    activeItemRestockDate,
    false
  );

  const estDateMinString = getEstDeliveyTimeEST(
    shippingType ?? 'Standard',
    false,
    activeItemRestockDate,
    true
  );
  const estDateMin = getEstDeliveyTimeEST(
    shippingType ?? 'Standard',
    true,
    activeItemRestockDate,
    true
  );

  if (!timelineChangesEnabled) {
    return estDateMaxString;
  }

  if (useCustomFormat && showRange) {
    return getCustomDeliveryDateRange(estDateMin, estDateMax);
  }

  let estDateRange = showRange ? formatDeliveryDateRange(estDateMin, estDateMax) : estDateMinString;

  if (!showRange && getMaxDate) {
    estDateRange = estDateMax;
  }

  return estDateRange;
};

/**
 * Function to get simple delivery date based on due date and shipping method
 * @param dueDate - The due date to calculate delivery from
 * @param shippingMethod - The shipping method ('standard' or 'expedited')
 * @param showRange - Whether to show as range or single date
 * @returns Formatted delivery date or range
 */
export const getRangeDeliveryDate = (
  dueDate: string,
  shippingMethod: string,
  showRange = false
) => {
  const baseDueDate = new Date(dueDate);

  // Add days based on shipping method
  const daysToSubtract = shippingMethod !== 'expedited' ? 3 : 2;

  if (showRange) {
    // For range, create min and max dates
    const minDate = new Date(baseDueDate);
    minDate.setDate(minDate.getDate() - daysToSubtract);

    const maxDate = new Date(baseDueDate);
    maxDate.setDate(maxDate.getDate());

    return formatDeliveryDateRange(minDate.toISOString(), maxDate.toISOString());
  }

  return getMonthAndDate(dueDate);
};

/**
 * Method to check whether any of the design item has the will require approval status for the collegiate marks.
 *
 * @param desLicensingRecords - list of design licensing records.
 */
export const getWillRequireStatusDesigns = (desLicensingRecords: IDesignLicenses[]) => {
  if (desLicensingRecords.length) {
    const statusChecks = desLicensingRecords.map(
      (desLicensing) => desLicensing.collegiate_status === 'will_require_approval'
    );

    return statusChecks.some((status) => status);
  }

  return false;
};

/**
 * Method to get client account label based on user account type
 */
export const getClientAccountLabel = (fallbackMessage = 'your account') => {
  const state: RootState = store.getState();
  const { user } = state.user;
  const { accountType, school, organization, business } = user;

  if (accountType === 'collegiate' && school && organization) {
    return `${organization} at ${school}`;
  }

  if (accountType === 'corporate' && business) {
    return business;
  }

  if (accountType === 'other' && organization) {
    return organization;
  }

  return fallbackMessage;
};

/** Get the pay later desc text (cart summary / cart layout). */
export const getPayLaterDescText = () => CART_SUMMARY_PAY_LATER_DESC_TEXT;

/**
 * Method to get client account name based on user account type
 */
export const getClientAccountName = () => {
  const state: RootState = store.getState();
  const { user } = state.user;
  const { accountType, school, organization, business } = user;

  if (accountType === 'collegiate' && school && organization) {
    return organization;
  }

  if (accountType === 'corporate' && business) {
    return business;
  }

  if (accountType === 'other' && organization) {
    return organization;
  }

  return '';
};

export const areAllStockValuesTrue = (stock: {
  [styleCode: string]: {
    [size: string]: boolean;
  };
}): boolean =>
  Object.values(stock).every((style) => Object.values(style).every((value) => value === true));

export const getOrderDeliveryDate = (
  shippingType?: keyof typeof SHIPPING_DAYS,
  activeItemRestockDate?: string
) => {
  const estDate = getEstDeliveyTimeEST(shippingType ?? 'Standard', false, activeItemRestockDate);
  return estDate;
};

/** Latest (chronologically greatest) ISO date string among non-empty inputs. */
const latestIsoString = (isoStrings: Array<string | undefined>): string | undefined => {
  const valid = isoStrings.filter((s): s is string => Boolean(s));
  if (valid.length === 0) return undefined;
  return valid.reduce((best, s) => (!best || new Date(s) > new Date(best) ? s : best));
};

/**
 * Computes timeline data filtered to only the SKUs the customer has ordered.
 * For each ship type, aggregates ordered SKUs across cart items:
 * - **min date:** latest (max) of each SKU's `newTimeline.minDate` — order cannot finish
 *   before the slowest "earliest" date.
 * - **max date:** latest (max) of each SKU's `newTimeline.maxDate` — end of the window.
 * Labels / est fallback prefer the SKU row with the latest max end date.
 */
export const computeSkuFilteredTimeline = (
  cartItems: ICartItemDetail[],
  orderedSizes: Record<string | number, Record<string, string | number>>
): ITimelineElement[] => {
  const byType: Record<string, ITimelineElement[]> = {};
  cartItems.forEach((item) => {
    const itemSizes = orderedSizes[item.designId] ?? orderedSizes[item.id];
    if (!item.skuShippingTimelines?.length || !itemSizes) return;

    item.skuShippingTimelines.forEach((sku: ISkuShippingTimeline) => {
      if (Number(itemSizes[sku.size] || 0) <= 0) return;

      sku.shippingTimeline.forEach((timeline) => {
        const type = (timeline.type ?? '').toLowerCase();
        if (!byType[type]) byType[type] = [];
        byType[type].push(timeline);
      });
    });
  });

  if (Object.keys(byType).length === 0) return [];

  return Object.entries(byType).map(([type, entries]) => {
    const eligible = entries.every((e) => e.eligible !== false);

    const aggregatedMinIso = latestIsoString(
      entries.map((e) => e.newTimeline?.minDate?.dateInISOString)
    );
    const aggregatedMaxIso = latestIsoString(
      entries.map((e) => e.newTimeline?.maxDate?.dateInISOString)
    );

    let referenceForLabels: ITimelineElement | undefined;
    let winningMaxIso: string | undefined;
    entries.forEach((entry) => {
      const maxDateStr = entry.newTimeline?.maxDate?.dateInISOString;
      if (maxDateStr && (!winningMaxIso || new Date(maxDateStr) > new Date(winningMaxIso))) {
        winningMaxIso = maxDateStr;
        referenceForLabels = entry;
      }
    });

    if (!referenceForLabels) {
      referenceForLabels =
        entries.reduce<ITimelineElement | undefined>((best, entry) => {
          const est = entry.estDeliveryDate?.dateInISOString;
          if (!est) return best;
          const bestEst = best?.estDeliveryDate?.dateInISOString;
          if (!bestEst) return entry;
          return new Date(est) > new Date(bestEst) ? entry : best;
        }, undefined) ?? entries[0];
    }

    let minDateStr = aggregatedMinIso ?? referenceForLabels.newTimeline?.minDate?.dateInISOString;
    let maxDateStr = aggregatedMaxIso ?? referenceForLabels.newTimeline?.maxDate?.dateInISOString;

    if (minDateStr && maxDateStr && new Date(minDateStr) > new Date(maxDateStr)) {
      minDateStr = referenceForLabels.newTimeline?.minDate?.dateInISOString;
      maxDateStr = referenceForLabels.newTimeline?.maxDate?.dateInISOString;
    }

    const timelineStr = referenceForLabels.newTimeline?.timeline ?? referenceForLabels.timeline;
    const estDateStr = referenceForLabels.estDeliveryDate?.dateInISOString;
    const { timelineRange } = referenceForLabels;

    const result: ITimelineElement = {
      type,
      eligible,
      ...(estDateStr && { estDeliveryDate: { dateInISOString: estDateStr } }),
      ...(timelineStr && { timeline: timelineStr }),
      ...(timelineRange && { timelineRange }),
    };

    if (minDateStr && maxDateStr) {
      result.newTimeline = {
        minDate: { dateInISOString: minDateStr },
        maxDate: { dateInISOString: maxDateStr },
        timeline: timelineStr ?? '',
      };
    }

    return result;
  });
};

export const getNewTimelineShippingRanges = (
  timelineData: ITimelineElement[],
  shippingType: string,
  showRange: boolean,
  restockDate?: string,
  getCustomFormat?: boolean,
  getISOFormat?: boolean,
  getMaxDate?: boolean,
  timelineChangesEnabled?: boolean
): string => {
  // Find the element with the maximum maxDate in newTimeline for the given shippingType
  if (!timelineData || timelineData.length === 0) {
    return '';
  }

  const maxDateElement = timelineData.find(
    (element) => (element.type ?? '').toLowerCase() === (shippingType ?? '').toLowerCase()
  );

  if (!maxDateElement) {
    const estDateMax = getEstDeliveyTimeEST(
      shippingType as keyof typeof SHIPPING_DAYS,
      true,
      '',
      false
    );
    return formatSingleDate(estDateMax) || '';
  }

  if (!timelineChangesEnabled) {
    const maxDateForOld = maxDateElement?.estDeliveryDate?.dateInISOString;
    return formatSingleDate(maxDateForOld ?? '');
  }

  let originalMinDate: Date | null = null;
  let originalMaxDate: Date | null = null;

  /** Prefer newTimeline (date range) when available - e.g. Standard "Mar 29 – Apr 5" */
  if (
    maxDateElement.newTimeline?.minDate?.dateInISOString &&
    maxDateElement.newTimeline?.maxDate?.dateInISOString
  ) {
    const minDate = new Date(maxDateElement.newTimeline.minDate.dateInISOString);
    const maxDate = new Date(maxDateElement.newTimeline.maxDate.dateInISOString);

    if (!Number.isNaN(minDate.getTime()) && !Number.isNaN(maxDate.getTime())) {
      originalMinDate = minDate;
      originalMaxDate = maxDate;
    }
  }

  if (
    originalMinDate &&
    originalMaxDate &&
    maxDateElement.timelineRange &&
    maxDateElement.timelineRange.minDays !== maxDateElement.timelineRange.maxDays
  ) {
    const sameLocalDay =
      originalMinDate.getFullYear() === originalMaxDate.getFullYear() &&
      originalMinDate.getMonth() === originalMaxDate.getMonth() &&
      originalMinDate.getDate() === originalMaxDate.getDate();

    if (sameLocalDay) {
      const base = new Date();
      const startOfToday = new Date(base.getFullYear(), base.getMonth(), base.getDate());
      const addCalendarDays = (days: number) => {
        const d = new Date(startOfToday);
        d.setDate(d.getDate() + days);
        return d;
      };
      originalMinDate = addCalendarDays(maxDateElement.timelineRange.minDays);
      originalMaxDate = addCalendarDays(maxDateElement.timelineRange.maxDays);
    }
  }

  const normalizedShippingType = (shippingType ?? '').toLowerCase();
  if (
    !originalMinDate &&
    !originalMaxDate &&
    AUTO_GUARANTEE_SHIP_TYPES.includes(normalizedShippingType) &&
    maxDateElement.estDeliveryDate?.dateInISOString
  ) {
    const flashDate = new Date(maxDateElement.estDeliveryDate.dateInISOString);
    if (!Number.isNaN(flashDate.getTime())) {
      originalMinDate = flashDate;
      originalMaxDate = flashDate;
    }
  }

  if (!originalMinDate && !originalMaxDate && maxDateElement.estDeliveryDate?.dateInISOString) {
    /** Fallback: use estDeliveryDate when newTimeline is missing (e.g. disabled MTO from quoter) */
    const singleDate = new Date(maxDateElement.estDeliveryDate.dateInISOString);
    if (!Number.isNaN(singleDate.getTime())) {
      originalMinDate = singleDate;
      originalMaxDate = singleDate;
    }
  }

  // Return empty string if dates couldn't be determined
  if (!originalMinDate || !originalMaxDate) {
    return '';
  }

  // Calculate differences from today
  const today = new Date();
  const minDateDiff = Math.ceil(
    (originalMinDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  const maxDateDiff = Math.ceil(
    (originalMaxDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  let finalMinDate: Date;
  let finalMaxDate: Date;

  if (restockDate && restockDate.trim() !== '') {
    // Add differences to restock date
    const restockDateObj = new Date(restockDate);

    // Validate restock date
    if (Number.isNaN(restockDateObj.getTime())) {
      // Fall back to original dates if restock date is invalid
      finalMinDate = originalMinDate;
      finalMaxDate = originalMaxDate;
    } else {
      finalMinDate = new Date(restockDateObj.getTime() + minDateDiff * 24 * 60 * 60 * 1000);
      finalMaxDate = new Date(restockDateObj.getTime() + maxDateDiff * 24 * 60 * 60 * 1000);
    }
  } else {
    // Use original dates if no restock date
    finalMinDate = originalMinDate;
    finalMaxDate = originalMaxDate;
  }

  // Final validation before returning
  if (Number.isNaN(finalMinDate.getTime()) || Number.isNaN(finalMaxDate.getTime())) {
    return '';
  }

  if (showRange) {
    if (getCustomFormat) {
      return getCustomDeliveryDateRange(finalMinDate.toISOString(), finalMaxDate.toISOString());
    }
    return formatDeliveryDateRange(finalMinDate.toISOString(), finalMaxDate.toISOString());
  }

  // Fix the logic: check for maxDate first, then regular ISO format
  if (getISOFormat && getMaxDate) return finalMaxDate.toISOString();
  if (getISOFormat) return finalMinDate.toISOString();

  // For guaranteed delivery (single date display), check if minimum date falls on weekend
  // Only apply weekend adjustment for Standard and Expedited shipping methods
  const normalizedType = (shippingType || '').toLowerCase();
  if (!showRange && (normalizedType === 'standard' || normalizedType === 'expedited')) {
    const adjustedMinDate = adjustWeekendToNextBusinessDay(finalMinDate);
    return formatSingleDate(adjustedMinDate.toISOString());
  }

  return formatSingleDate(finalMinDate.toISOString());
};

/**
 * Function to get order header secondary text
 * @param isDesktop - if desktop
 * @param isGroupOrder - if group order
 */
export const getOrderHeaderSecondaryText = (
  isDesktop: boolean,
  isGroupOrder: boolean,
  shippingType?: keyof typeof SHIPPING_DAYS,
  activeItemRestockDate?: string,
  showRange?: boolean,
  timelineChangesEnabled?: boolean,
  cartItems?: ICartItemDetail[]
) => {
  const estDateRange = getNewTimelineShippingRanges(
    cartItems && cartItems?.length > 0 ? cartItems[0]?.timelineData ?? [] : [],
    shippingType ?? '',
    showRange ?? false,
    activeItemRestockDate,
    false,
    false,
    false,
    timelineChangesEnabled
  );

  const regularOrderText = isDesktop
    ? `Hey! If you order today, you'll get it delivered by ${estDateRange}. Need any help?`
    : `Order today & get it by ${estDateRange}.`;
  const groupOrderText = GROUP_ORDER_HEADER_SECONDARY_TEXT;

  return isGroupOrder ? groupOrderText : regularOrderText;
};
